'use client';

import { useRecoilValue } from 'recoil';
import { selectedProjectDataState, isProjectCompleteState } from '@/lib/state';

import ActiveUserBidsDropdown from '@/components/ActiveUserBidsDropdown';
import BidBox from '@/components/BidBox';
import BidProgressBar from '@/components/BidProgressBar';
import ClientOnly from '@/components/ClientOnly';
import GlobalRefreshIndicator from '@/components/GlobalRefreshIndicator';
import Markdown from '@/components/Markdown';
import PrizePool from '@/components/PrizePool';
import ProjectFooter from '@/components/ProjectFooter';
import ProjectImage from '@/components/ProjectImage';
import ProjectPhases from '@/components/ProjectPhases';
import ProjectStatistics from '@/components/ProjectStatistics';
import ProjectTitle from '@/components/ProjectTitle';
import ProjectPageSkeleton from '@/components/ProjectPageSkeleton';
import TransactionProcessor from '@/components/TransactionProcessor';

export default function ProjectPage() {
  const project = useRecoilValue(selectedProjectDataState);
  const isProjectComplete = useRecoilValue(isProjectCompleteState);

  if (!project) {
    return (
      <ClientOnly>
        <ProjectPageSkeleton />
      </ClientOnly>
    );
  }

  return (
    <div className="z-10">
      <div className="flex flex-col space-y-4">
        {/* Heading */}
        <div className="flex justify-between">
          <div className="flex flex-col px-4 md:px-0 space-y-2 flex-wrap md:flex-nowrap">
            <ProjectTitle />
            <ProjectStatistics />
          </div>
          <div className="hidden md:flex flex-col items-end justify-end flex-grow">
            {!isProjectComplete && <GlobalRefreshIndicator userCanRefresh={true} />}
          </div>
        </div>

        <div className="flex w-full space-x-4 justify-center flex-wrap-reverse md:flex-nowrap">
          {/* Left column */}
          <div className="flex flex-col items-center w-[350px] md:max-w-[420px] md:w-[420px] md:space-y-4">
            <div className="flex flex-col w-full border border-neutral bg-base-100 rounded mt-4 md:mt-0">
              <PrizePool
                showPot={project.show_pot}
                showFeesPot={project.show_fees_pot}
                token={project.token}
                decimals={project.decimals}
              />
              <BidProgressBar token={project.token} decimals={project.decimals} />
              <ActiveUserBidsDropdown pid={project.id} />
            </div>

            <div className="h-2" />
            <div className="flex flex-col w-full border border-neutral bg-base-100 rounded">
              <BidBox token={project.token} decimals={project.decimals} />
            </div>
          </div>

          {/* Right Column */}
          <div className="flex flex-col items-center w-[300px] sm:w-[350px] md:w-[420px] space-y-4">
            <ProjectImage project={project} />
            <div className="w-full max-w-[350px]">
              <ProjectPhases />
            </div>
            <div className="mt-4 font-light max-w-[380px]">
              <Markdown>{project.bulletpoints}</Markdown>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="h-8" />
        <div className="max-w-[380px] md:max-w-screen-md">
          <ProjectFooter project={project} />
        </div>
      </div>
      <ClientOnly>
        <TransactionProcessor />
      </ClientOnly>
    </div>
  );
}
